import useEmblaCarousel from 'embla-carousel-react'
import React, { useCallback, useEffect, useState } from 'react'
import { useSelector } from 'react-redux'
import Link from 'next/link'

import Loading from 'components/Loading/index.js'
import DateCard from 'components/Tiles/UpcomingVisitsWidget/DateCard'
import CarouselDotGroup from 'components/Tiles/UpcomingVisitsWidget/CarouselDotGroup'
import { getCheckinSuccess } from 'stores/Appointments/Select'

import { mergeStyles } from '@enlyt/helpers/mergeStyles'
import _ from 'lodash'
import themeStyles from 'components/Tiles/UpcomingVisitsWidget/styles'
import { useTheme } from 'contexts/ThemeProvider'
import localization from 'components/Tiles/UpcomingVisitsWidget/localization'
import { getDependentDetailsLoading } from 'stores/Account/Select'
import withLocalization from '@web/helpers/localizationUtils'

export const RenderEmptyComponent = text => {
  const { theme } = useTheme()
  const defaultStyles = themeStyles(theme)
  return (
    <div style={defaultStyles.noDataContainer}>
      <p style={defaultStyles.noDataText}>{text}</p>
    </div>
  )
}

const UpcomingVisitsWidget = ({ t, ...props }) => {
  const { theme } = useTheme()
  const defaultStyles = themeStyles(theme)
  const checkIn = useSelector(getCheckinSuccess)
  const UpcomingVisits = _.clone(props.latestUpcomingAppointments || [])
  if (UpcomingVisits.length > 3) {
    UpcomingVisits.length = 3
  }
  const linkTo = props.linkTo
  const errorUpcomingAppointments = props.errorUpcomingAppointments
  const currCheckinAppointmentId = props.currCheckinAppointmentId
  const loadingCheckin = props.loadingCheckin
  const styles = mergeStyles(defaultStyles, props.style)
  const [indexSelected, setIndexSelected] = useState(0)
  const dependentDetailsLoading = useSelector(getDependentDetailsLoading)

  const [emblaRef, emblaApi] = useEmblaCarousel({
    align: 'start',
    loop: false,
    skipSnaps: false,
    dragFree: false
  })

  const scrollTo = useCallback(
    index => {
      emblaApi?.scrollTo(index, true)
    },
    [emblaApi]
  )

  // Sync scroll position to initial index when carousel is ready (once).
  useEffect(() => {
    if (!emblaApi) {
      return
    }
    emblaApi.scrollTo(indexSelected, true)
  }, [emblaApi]) // eslint-disable-line react-hooks/exhaustive-deps -- only on mount/embla ready

  // Sync state from carousel when user drags. Defer setState to avoid "Maximum update depth" if select fires during ref detach.
  useEffect(() => {
    if (!emblaApi) {
      return
    }
    const onSelect = () => {
      const snap = emblaApi.selectedScrollSnap()
      queueMicrotask(() => setIndexSelected(snap))
    }
    emblaApi.on('select', onSelect)
    return () => {
      emblaApi.off('select', onSelect)
    }
  }, [emblaApi])

  const handleDotClick = useCallback(
    idx => {
      setIndexSelected(idx)
      scrollTo(idx)
    },
    [scrollTo]
  )

  if (errorUpcomingAppointments) {
    return (
      <div style={styles.containerOut}>
        <div style={styles.errorContainer}>
          <p style={styles.errorText}>{t('somethingWentWrong')}</p>
        </div>
      </div>
    )
  }

  const RenderEmptyComponentWithTitle = text => {
    return (
      <>
        <div style={styles.title}>{t('upcomingVisits')}</div>
        {RenderEmptyComponent(text || t('noData'))}
      </>
    )
  }

  const loading =
    (props.loading && _.isEmpty(UpcomingVisits)) || dependentDetailsLoading

  return (
    <div style={styles.containerOut}>
      {loading && <Loading />}
      <div
        style={
          UpcomingVisits.length
            ? styles.topContainer
            : styles.noDataTopContainer
        }
      >
        {loading ? (
          RenderEmptyComponentWithTitle()
        ) : UpcomingVisits.length ? (
          <>
            <div style={styles.title}>{t('upcomingVisits')}</div>
            <div style={styles.carousel}>
              <div style={{ overflow: 'hidden', width: '100%' }} ref={emblaRef}>
                <div
                  style={{ display: 'flex', touchAction: 'pan-y pinch-zoom' }}
                >
                  {UpcomingVisits.map((appointment, i) => (
                    <div key={i} style={{ flex: '0 0 100%', minWidth: 0 }}>
                      <DateCard
                        loadingCheckin={loadingCheckin}
                        currCheckinAppointmentId={currCheckinAppointmentId}
                        workflow={checkIn.find(
                          appo =>
                            appo.appointmentId === appointment.appointmentId
                        )}
                        index={i}
                        date={appointment.date}
                        time={appointment.time}
                        timeZone={appointment.timeZone}
                        appointmentId={appointment.appointmentId}
                        appointmentType={appointment.appointmentType}
                        providerName={appointment.providerName}
                        linkTo={linkTo}
                      />
                    </div>
                  ))}
                </div>
              </div>
              <CarouselDotGroup
                slidesCount={UpcomingVisits.length}
                onClick={handleDotClick}
                indexSelected={indexSelected}
              />
            </div>
          </>
        ) : (
          RenderEmptyComponentWithTitle()
        )}
      </div>
      <div style={styles.bottomContainer}>
        <Link href={linkTo} style={styles.link}>
          {t('viewAll')}
        </Link>
      </div>
    </div>
  )
}

export default withLocalization(UpcomingVisitsWidget, localization)
